import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getClientToken } from '../api/axios';
import { LienRetour, Message } from '../components/charte/Elements';
import BoutonAide from '../components/charte/BoutonAide';
import ResumeModifiable, { manquesResume, normaliserResume, ResumeMission } from '../components/ResumeModifiable';
import { AIDE } from '../aide/textesAide';

type Step = 'PROMPT' | 'CLARIFICATION' | 'SUMMARY' | 'SUBMIT';

interface ClarificationQuestion {
  id: string;
  texte: string;
  options: string[];
  multiple: boolean;
}

type MissionSummary = ResumeMission;

const EXAMPLES = [
  'Pharmacies de Pikine et leurs horaires',
  'Restaurants du Plateau, menus et contacts',
  'Points Wave actifs aux HLM',
  'Écoles privées de Guédiawaye avec effectifs',
];

/* Ancien quota de 3 demandes compté dans le navigateur : retiré. Seul le
   serveur limite les générations (admin-service, 429 avec un message destiné
   au client). La clé est effacée des navigateurs qui l'avaient gardée. */
const ANCIENNE_CLE_QUOTA = 'tribe_ia_quota_remaining';

const SYSTEM_CLARIFICATION = `Tu es l'assistant de Tribe, plateforme de collecte de données terrain. Tribe opère actuellement uniquement au Sénégal. Toutes les zones géographiques proposées doivent être au Sénégal. Quand le client mentionne un lieu ambigu (ex: 'Plateau' qui existe à Dakar ET à Abidjan), interprète-le SYSTÉMATIQUEMENT comme le lieu au Sénégal. Ne mentionne JAMAIS un autre pays africain, même en exemple. Pour Plateau, il s'agit de la Commune du Plateau à Dakar. Un client vient de soumettre une demande. Pose 2-3 questions de clarification sous forme de choix multiples pour affiner sa demande. Les questions doivent porter sur : 1) La zone géographique précise (propose des quartiers de Dakar pertinents par rapport à sa demande), 2) Le niveau de détail souhaité (nom + adresse / + photos / + horaires / + contacts). Réponds UNIQUEMENT en JSON avec ce format : { "questions": [{ "id": "string", "texte": "string", "options": ["string"], "multiple": false }] }`;

/* Le champ 'produits' suit la même logique que 'zone', d'un cran plus strict :
   le modèle choisit dans une liste fermée que le prompt ne porte pas. C'est
   admin-service qui l'ajoute au prompt système, lue dans la base du référentiel
   au moment de l'appel (drapeau referentielProduits), puis location-service qui
   résout les libellés en identifiants. Le navigateur n'a donc aucune liste de
   produits à connaître, à charger, ni à maintenir à jour. */

/* Le champ 'zone' est résolu côté serveur (ZoneResolverService) : Nominatim
   restreint au Sénégal, puis containment dans la région de Dakar. Le modèle n'a
   donc plus à se garder lui-même des autres pays — il nomme une zone, le serveur
   tranche. Les exemples ci-dessous sont tous dans la borne. */
const SYSTEM_SUMMARY = `Génère un résumé qualitatif et vendeur de cette mission de collecte de données terrain. Le champ 'zone' doit contenir UNIQUEMENT le nom de la localité ou du quartier concerné, tel qu'on le chercherait sur une carte : pas de pays, pas de région, pas de virgule, aucune précision ajoutée. Exemples de valeurs attendues : "Plateau", "Grand Yoff", "Pikine", "Rufisque". Réponds UNIQUEMENT en JSON avec ce format exact : { "titre": "string", "zone": "string", "description": "string", "typeDonnees": ["string"], "champsCollecte": ["string"], "produits": ["string"], "collecteursEstimes": 5, "delaiEstime": "string", "valeurAjoutee": "string" }`;

/* Ces deux appels partent en fetch() brut, donc hors de l'intercepteur qui
   pose le Bearer sur locationApi. Ils visent admin-service, qui contourne
   aujourd'hui l'authentification sur /claude/chat et /mission/ia-request ;
   le jeton est ajouté ici en prérequis du retrait de ces contournements. */
const enTetesAuth = (): Record<string, string> => {
  const token = getClientToken();
  return {
    'content-type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

class ErreurGeneration extends Error {
  constructor(public statut: number, public messageClient: string | null) {
    super(`API ${statut}`);
  }
}

/** Refus du serveur (429 : limite du jour ou plafond mensuel) : son message, rédigé
    pour le client, s'affiche tel quel. Session refusée : on le dit. Sinon le
    message générique, jamais le texte technique d'une erreur. */
export const messageErreur = (e: unknown, generique: string) => {
  if (e instanceof ErreurGeneration) {
    if (e.statut === 429 && e.messageClient) return e.messageClient;
    if (e.statut === 401 || e.statut === 403) return 'Votre session ne permet pas cette demande. Reconnectez-vous puis réessayez.';
  }
  if (e instanceof TypeError) return 'Impossible de joindre Tribe. Vérifiez votre connexion puis réessayez.';
  return generique;
};

async function callClaude(
  system: string,
  userMessage: string,
  referentielProduits = false,
): Promise<string> {
  const res = await fetch('https://dev-admin.tribe.sn/claude/chat', {
    method: 'POST',
    headers: enTetesAuth(),
    body: JSON.stringify({
      system,
      messages: [{ role: 'user', content: userMessage }],
      referentielProduits,
    }),
  });
  if (!res.ok) {
    // Refus explicites du serveur (quota du jour, plafond mensuel) : le message
    // est destiné au client et s'affiche tel quel.
    const corps = await res.json().catch(() => null);
    throw new ErreurGeneration(res.status, typeof corps?.error === 'string' ? corps.error : null);
  }
  const data = await res.json();
  return data.content[0].text as string;
}

function parseJson<T>(raw: string): T {
  const match = raw.match(/\{[\s\S]*\}/);
  if (!match) throw new Error('JSON introuvable dans la réponse');
  return JSON.parse(match[0]) as T;
}

/* ── Component ── */
/* Présentation des maquettes NouvelleMission 1 à 3 : étapes numérotées en
   haut à droite, colonne principale et encadrés à droite. L'assistant
   n'apparaît qu'à l'étape 2, avec ses questions. */

const ETAPES = ['Décrire', 'Préciser', 'Relire et envoyer'];
const NUMERO_ETAPE: Record<Step, number> = { PROMPT: 0, CLARIFICATION: 1, SUMMARY: 2, SUBMIT: 3 };
const NOMBRES = ['Aucune', 'Une', 'Deux', 'Trois', 'Quatre', 'Cinq'];

const IconeFleche = () => (
  <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
);
const IconeCoche = () => (
  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="M5 12.5l4.5 4.5L19 7" /></svg>
);

const Etapes: React.FC<{ courante: number }> = ({ courante }) => (
  <ol aria-label="Étapes" className="etapes">
    {ETAPES.map((e, i) => (
      <React.Fragment key={e}>
        {i > 0 && <li aria-hidden className="etapes__trait" style={{ background: i <= courante ? 'var(--vert)' : 'var(--bordure)' }} />}
        <li aria-current={i === courante ? 'step' : undefined}
          className={`etapes__etape${i < courante ? ' etapes__etape--faite' : i === courante ? ' etapes__etape--courante' : ''}`}>
          <span className="etapes__rond">{i < courante ? <IconeCoche /> : i + 1}</span>{e}
        </li>
      </React.Fragment>
    ))}
  </ol>
);

const Encadre: React.FC<{ etiquette: string; fort?: boolean; couleur: string; children: React.ReactNode }> = ({ etiquette, fort, couleur, children }) => (
  <div className={fort ? 'carte carte--forte' : 'carte'} style={{ padding: fort ? '22px 24px' : '20px 22px', gap: '12px' }}>
    <span className="etiquette-mono" style={{ color: couleur }}>{etiquette}</span>
    {children}
  </div>
);

const QuestionAvant = () => (
  <p className="petit" style={{ color: 'var(--gris)', padding: '0 4px' }}>
    Une question avant de commander ? <a href="mailto:missions@tribe.sn?subject=Mise en relation — Mission terrain" style={{ fontWeight: 600 }}>missions@tribe.sn</a>
  </p>
);

const NewMission: React.FC = () => {
  const navigate = useNavigate();
  const { user }  = useAuth();

  const [step,       setStep]       = useState<Step>('PROMPT');
  const [promptText, setPromptText] = useState('');
  const [questions,  setQuestions]  = useState<ClarificationQuestion[]>([]);
  const [answers,    setAnswers]    = useState<Record<string, string[]>>({});
  const [summary,    setSummary]    = useState<MissionSummary | null>(null);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [missionCreee, setMissionCreee] = useState<string | null>(null);

  useEffect(() => {
    try { localStorage.removeItem(ANCIENNE_CLE_QUOTA); } catch { /* stockage indisponible */ }
  }, []);

  const hautRef = useRef<HTMLDivElement>(null);

  useEffect(() => { document.title = 'Nouvelle mission — Tribe'; }, []);

  // Changement d'étape : retour en haut de la page.
  useEffect(() => {
    hautRef.current?.scrollIntoView?.({ block: 'start' });
  }, [step]);

  /* ── Step 1 → 2 ── */
  const handlePromptSubmit = async () => {
    if (!promptText.trim()) return;
    setLoading(true);
    setError('');
    try {
      const raw = await callClaude(SYSTEM_CLARIFICATION, promptText);
      const parsed = parseJson<{ questions: ClarificationQuestion[] }>(raw);
      setQuestions(parsed.questions ?? []);
      setStep('CLARIFICATION');
    } catch (e) {
      setError(messageErreur(e, 'Erreur lors de la génération des questions. Vérifiez votre connexion.'));
    } finally {
      setLoading(false);
    }
  };

  /* ── Step 2 → 3 ── */
  const handleClarificationSubmit = async () => {
    setLoading(true);
    setError('');
    const answersText = questions
      .map(q => `${q.texte}: ${(answers[q.id] ?? []).join(', ')}`)
      .join('\n');
    try {
      const raw = await callClaude(
        SYSTEM_SUMMARY,
        `Prompt client : ${promptText}\nRéponses : ${answersText}`,
        true,
      );
      setSummary(normaliserResume(parseJson<unknown>(raw)));
      setStep('SUMMARY');
    } catch (e) {
      setError(messageErreur(e, 'Erreur lors de la génération du résumé. Veuillez réessayer.'));
    } finally {
      setLoading(false);
    }
  };

  /* ── Step 3 → 4 ── */
  const handleSubmit = async () => {
    if (!summary) return;
    const manques = manquesResume(summary);
    if (manques.length > 0) {
      setError(`Complétez ${manques.join(', ')} avant d'envoyer la demande.`);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await fetch('https://dev-admin.tribe.sn/mission/ia-request', {
        method: 'POST',
        headers: enTetesAuth(),
        body: JSON.stringify({
          prompt: promptText,
          reponses: questions.map(q => ({ question: q.texte, reponse: answers[q.id] ?? [] })),
          resume: summary,
          email: user?.email,
          tags: ['ia-generated'],
          statut: 'en_attente_etude',
        }),
      });

      /* La réponse était ignorée : l'écran de confirmation s'affichait même
         quand aucune quête n'avait été créée côté location-service. */
      const data = await res.json().catch(() => null);
      if (!res.ok || data?.success === false) {
        console.error('[NewMission] soumission en échec', res.status, data);
        // 502 avec un id : la demande est enregistrée, seule la création de la
        // mission a échoué ; Tribe la reprend depuis le back-office.
        if (res.status === 502 && data?.id) {
          setMissionCreee(null);
          setStep('SUBMIT');
          return;
        }
        throw new ErreurGeneration(res.status, typeof data?.error === 'string' ? data.error : null);
      }

      setMissionCreee(typeof data?.questId === 'string' ? data.questId : null);
      setStep('SUBMIT');
    } catch (e) {
      setError(messageErreur(e, "Votre demande n'a pas pu être envoyée. Réessayez dans un instant."));
    } finally {
      setLoading(false);
    }
  };

  const toggleAnswer = (qId: string, option: string, multiple: boolean) => {
    setAnswers(prev => {
      const cur = prev[qId] ?? [];
      if (multiple) {
        return { ...prev, [qId]: cur.includes(option) ? cur.filter(o => o !== option) : [...cur, option] };
      }
      return { ...prev, [qId]: [option] };
    });
  };

  const allAnswered = questions.length > 0 && questions.every(q => (answers[q.id] ?? []).length > 0);

  /* ── Rendu ── */
  const numero = NUMERO_ETAPE[step];
  const nouvelleDemande = () => {
    setStep('PROMPT');
    setPromptText('');
    setQuestions([]);
    setAnswers({});
    setSummary(null);
    setMissionCreee(null);
    setError('');
  };
  const retourEtape = (vers: Step) => { setStep(vers); setError(''); };

  const lienRetour = step === 'CLARIFICATION'
    ? <button type="button" className="lien-retour lien-retour--bouton" onClick={() => retourEtape('PROMPT')} disabled={loading}><IconeRetour />Modifier ma description</button>
    : step === 'SUMMARY'
      ? <button type="button" className="lien-retour lien-retour--bouton" onClick={() => retourEtape('CLARIFICATION')} disabled={loading}><IconeRetour />Revenir aux précisions</button>
      : <LienRetour vers="/missions">Missions</LienRetour>;

  return (
    <div className="page" style={{ gap: '28px' }}>
      <div ref={hautRef} className="ligne ligne--entre" style={{ gap: '12px' }}>
        {lienRetour}
        <Etapes courante={numero} />
      </div>

      <div className="grille-creation">
        {/* ═══ Étape 1 : décrire ═══ */}
        {step === 'PROMPT' && (
          <>
            <section className="pile" style={{ gap: '22px' }}>
              <div className="pile" style={{ gap: '8px' }}>
                <div className="ligne" style={{ gap: '10px', flexWrap: 'nowrap', alignItems: 'flex-start' }}>
                  <h1>Qu'est-ce que vous voulez savoir du terrain ?</h1>
                  <BoutonAide texte={AIDE.nouvelleMission} />
                </div>
                <p className="creation__intro">
                  Dites-le avec vos mots. Quels lieux, où, quelles informations sur chacun. L'assistant Tribe pose ensuite deux ou trois
                  questions pour cadrer la mission, puis vous relisez avant d'envoyer.
                </p>
              </div>

              <div className="pile" style={{ gap: '10px' }}>
                <label htmlFor="besoin" className="etiquette-mono" style={{ fontSize: '11px', color: 'var(--orange)' }}>Votre besoin</label>
                <textarea id="besoin" className="creation__besoin" rows={7} value={promptText}
                  onChange={e => setPromptText(e.target.value)}
                  placeholder="Exemple : recenser toutes les pharmacies de Pikine, avec leurs horaires, le nom du responsable et la disponibilité des antipaludéens." />
                {error && <Message genre="erreur">{error}</Message>}
                <div className="ligne ligne--entre" style={{ gap: '12px' }}>
                  <span className="petit" style={{ color: 'var(--gris)' }}>Plus vous êtes précis sur la zone et les informations attendues, plus le devis sera juste.</span>
                  <button type="button" className="bouton bouton--grand" onClick={handlePromptSubmit} disabled={!promptText.trim() || loading}>
                    {loading ? <><span className="roue roue--claire" aria-hidden /> Analyse en cours…</> : <>Continuer<IconeFleche /></>}
                  </button>
                </div>
              </div>

              <div className="pile" style={{ gap: '10px', paddingTop: '6px' }}>
                <span className="petit" style={{ color: 'var(--gris)' }}>Ou partez d'un exemple :</span>
                <div className="ligne" style={{ gap: '8px' }}>
                  {EXAMPLES.map(ex => (
                    <button key={ex} type="button" className="pastille-choix" onClick={() => setPromptText(ex)}>{ex}</button>
                  ))}
                </div>
              </div>
            </section>

            <aside className="pile creation__cote">
              <Encadre etiquette="Ce qui se passe ensuite" fort couleur="var(--ocre-clair)">
                <ol className="creation__suite">
                  <li><span>1</span><div><strong>Vous décrivez</strong>, l'assistant précise la zone, les lieux et les informations attendues.</div></li>
                  <li><span>2</span><div><strong>Tribe étudie la demande</strong> et vous envoie un devis sous 48 h.</div></li>
                  <li><span>3</span><div><strong>Vous signez</strong> depuis le portail, et la collecte démarre à la date fixée dans le devis.</div></li>
                </ol>
              </Encadre>
              <Encadre etiquette="Ce que Tribe sait collecter" couleur="var(--vert)">
                <p style={{ fontSize: '14px', lineHeight: 1.55 }}>
                  Tout lieu physique et ce qu'on y observe : commerces, structures de santé, écoles, points de service, infrastructures.
                  Horaires, responsable, prix, disponibilité d'un produit, état des lieux, photo horodatée.
                </p>
                <p className="petit" style={{ color: 'var(--gris)' }}>Chaque donnée est vérifiée deux fois avant de vous être livrée.</p>
              </Encadre>
              <QuestionAvant />
            </aside>
          </>
        )}

        {/* ═══ Étape 2 : préciser ═══ */}
        {step === 'CLARIFICATION' && (
          <>
            <section className="pile" style={{ gap: '22px' }}>
              <div className="pile" style={{ gap: '8px' }}>
                <h1 className="creation__titre">{NOMBRES[questions.length] ?? questions.length} précision{questions.length > 1 ? 's' : ''} pour cadrer la mission.</h1>
                <p className="creation__intro">
                  L'assistant a lu votre description. Choisissez les réponses qui vous conviennent ; vous pourrez tout corriger à l'étape suivante.
                </p>
              </div>

              <div className="carte" style={{ padding: '18px 20px', gap: '6px' }}>
                <span className="etiquette-mono" style={{ color: 'var(--texte-3)' }}>Votre description</span>
                <p style={{ fontSize: '15px', lineHeight: 1.5 }}>{promptText}</p>
              </div>

              <div className="pile" style={{ gap: '18px' }}>
                {questions.map((q, i) => (
                  <div key={q.id} className="pile" style={{ gap: '10px' }}>
                    <div className="ligne" style={{ gap: '10px', alignItems: 'baseline', flexWrap: 'nowrap' }}>
                      <span className="creation__numero">{i + 1}</span>
                      <span id={`question-${q.id}`} style={{ fontWeight: 600, fontSize: '16px' }}>{q.texte}</span>
                    </div>
                    <div className="ligne" style={{ gap: '8px' }} role="group" aria-labelledby={`question-${q.id}`}>
                      {q.options.map(opt => {
                        const on = (answers[q.id] ?? []).includes(opt);
                        return (
                          <button key={opt} type="button" aria-pressed={on} className="pastille-choix pastille-choix--reponse"
                            onClick={() => toggleAnswer(q.id, opt, q.multiple)}>
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                    {q.multiple && <p className="petit" style={{ color: 'var(--gris)' }}>Plusieurs choix possibles</p>}
                  </div>
                ))}
              </div>

              {error && <Message genre="erreur">{error}</Message>}

              <div className="ligne" style={{ justifyContent: 'flex-end', gap: '14px', paddingTop: '6px' }}>
                <button type="button" className="lien-gris" onClick={() => retourEtape('PROMPT')} disabled={loading}>Retour</button>
                <button type="button" className="bouton bouton--grand" onClick={handleClarificationSubmit} disabled={!allAnswered || loading}>
                  {loading ? <><span className="roue roue--claire" aria-hidden /> Préparation du résumé…</> : <>Voir le résumé<IconeFleche /></>}
                </button>
              </div>
            </section>

            <aside className="pile creation__cote">
              <Encadre etiquette="Pourquoi ces questions" fort couleur="var(--ocre-clair)">
                <p style={{ fontSize: '14px', lineHeight: 1.5, color: 'rgba(242,232,212,0.85)' }}>
                  La zone fixe le nombre de Spotters à mobiliser. Les informations attendues fixent le protocole de collecte. La
                  fréquence décide si la mission vit dans le temps ou se livre en une fois.
                </p>
              </Encadre>
              <QuestionAvant />
            </aside>
          </>
        )}

        {/* ═══ Étape 3 : relire et envoyer ═══ */}
        {step === 'SUMMARY' && summary && (
          <>
            <section className="pile" style={{ gap: '22px' }}>
              <div className="pile" style={{ gap: '8px' }}>
                <h1 className="creation__titre">Votre demande, telle que Tribe va la lire.</h1>
                <p className="creation__intro">Chaque champ se modifie directement. Rien ne part avant que vous cliquiez sur « Envoyer la demande ».</p>
              </div>

              <ResumeModifiable resume={summary} onChange={setSummary} />

              {error && <Message genre="erreur">{error}</Message>}

              <div className="ligne ligne--entre" style={{ gap: '14px', paddingTop: '6px' }}>
                <span className="ligne" style={{ gap: '8px', fontSize: '14px', color: 'var(--texte-2)', flexWrap: 'nowrap' }}>
                  <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
                  Tribe vous envoie un devis sous 48 h. Aucun engagement avant sa signature.
                </span>
                <button type="button" className="bouton bouton--grand" onClick={handleSubmit} disabled={loading}>
                  {loading ? <><span className="roue roue--claire" aria-hidden /> Envoi…</> : <>Envoyer la demande<IconeFleche /></>}
                </button>
              </div>
            </section>

            <aside className="pile creation__cote">
              <Encadre etiquette="Après l'envoi" fort couleur="var(--ocre-clair)">
                <p style={{ fontSize: '14px', lineHeight: 1.5, color: 'rgba(242,232,212,0.85)' }}>
                  La mission apparaît dans vos Missions avec le statut « En étude ». Vous recevez un courriel quand le devis est prêt,
                  et il apparaît dans Documents, à signer depuis le portail.
                </p>
              </Encadre>
              <Encadre etiquette="Ce que vous recevrez" couleur="var(--vert)">
                <p style={{ fontSize: '14px', lineHeight: 1.55 }}>
                  Un suivi en direct sur votre fiche mission et un export Excel des lieux validés.
                </p>
              </Encadre>
              <QuestionAvant />
            </aside>
          </>
        )}

        {/* ═══ Demande envoyée ═══ */}
        {step === 'SUBMIT' && (
          <>
            <section className="pile" style={{ gap: '22px' }} role="status">
              <div className="pile" style={{ gap: '8px' }}>
                <span className="etiquette-mono" style={{ fontSize: '11px', color: 'var(--orange)' }}>Demande transmise à Tribe</span>
                <h1 className="creation__titre">Demande envoyée.</h1>
                <p className="creation__intro">
                  L'équipe Tribe a bien reçu votre demande. Vous recevrez un devis sous 48 h, à signer dans Documents ;
                  la collecte commence après votre signature.
                </p>
              </div>
              <div className="ligne" style={{ gap: '12px' }}>
                {missionCreee
                  ? <button type="button" className="bouton bouton--secondaire" onClick={() => navigate(`/missions/${missionCreee}`)}>Voir ma demande</button>
                  : <button type="button" className="bouton bouton--secondaire" onClick={() => navigate('/missions')}>Mes missions</button>}
                <button type="button" className="bouton" onClick={nouvelleDemande}>Nouvelle demande</button>
              </div>
            </section>
            <aside className="pile creation__cote">
              <Encadre etiquette="Et maintenant" fort couleur="var(--ocre-clair)">
                <p style={{ fontSize: '14px', lineHeight: 1.5, color: 'rgba(242,232,212,0.85)' }}>
                  La mission apparaît dans vos Missions avec le statut « En étude ». Vous recevez un courriel quand le devis est prêt.
                </p>
              </Encadre>
            </aside>
          </>
        )}
      </div>
    </div>
  );
};

const IconeRetour = () => (
  <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M15 5l-7 7 7 7" /></svg>
);

export default NewMission;
